/**
 * React Query hook for fetching feature flags from the backend.
 */

import { useQuery } from "@tanstack/react-query";
import { publicApiClient } from "@/services/api";
import type { FeatureFlags } from "@/types/features";

/**
 * Hook to fetch feature flags from the backend.
 * Flags are cached for the session and can be used to conditionally render features.
 *
 * @returns Object with feature flags data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data: flags, isLoading } = useFeatureFlags();
 *
 * if (flags?.demo_flow) {
 *   return <DemoFlowPage />;
 * }
 * ```
 */
export function useFeatureFlags() {
  return useQuery<FeatureFlags>({
    queryKey: ["feature-flags"],
    queryFn: async () => {
      const data = await publicApiClient.get<FeatureFlags>("/api/v1/features");
      return data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });
}
